import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, TrendingUp, Target, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';

const SSEMRoadmap = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    fetchAssessment();
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${assessmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssessment(response.data);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      showError('Error loading assessment');
      navigate('/ssem');
    } finally {
      setLoading(false);
    }
  };

  const getImpactColor = (impact) => {
    return impact === 'High' ? 'bg-red-100 text-red-700' :
           impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
           'bg-green-100 text-green-700';
  };

  const getImpactIcon = (impact) => {
    return impact === 'High' ? '🔴' :
           impact === 'Medium' ? '🟡' : '🟢';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!assessment || !assessment.improvement_roadmap || assessment.improvement_roadmap.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="outline"
            onClick={() => navigate('/ssem')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Excellent Performance!</h2>
            <p className="text-gray-600">
              All dimensions are at Level 4 (Measured) or above. No critical improvements needed at this time.
            </p>
            <Button
              onClick={() => navigate('/ssem')}
              className="mt-6 bg-blue-600 hover:bg-blue-700"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/ssem')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">AI-Generated Improvement Roadmap</h1>
                <p className="text-gray-600 mt-1">Period: {assessment.period_tag}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium mb-1">Overall Level</p>
                <p className="text-2xl font-bold text-blue-900">Level {assessment.overall_level}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-600 font-medium mb-1">Action Items</p>
                <p className="text-2xl font-bold text-orange-900">{assessment.improvement_roadmap.length}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium mb-1">Status</p>
                <p className="text-2xl font-bold text-green-900">Ready</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {assessment.improvement_roadmap.map((item, index) => (
            <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-blue-600">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{item.dimension}</h3>
                      <p className="text-blue-100 text-sm">
                        Level {item.current_level} → Level {item.target_level}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getImpactColor(item.expected_impact)}`}>
                    {getImpactIcon(item.expected_impact)} {item.expected_impact} Impact
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <Target className="w-4 h-4 text-red-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Current State</h4>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-900">
                        Your organization is currently at <strong>Level {item.current_level}</strong> for {item.dimension}.
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Target State</h4>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-900">
                        Goal: Achieve <strong>Level {item.target_level}</strong> maturity for {item.dimension}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Recommended Action</h4>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-gray-900 mb-3">{item.key_action}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-blue-700 font-medium">Digital Tool:</span>
                      <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">
                        {item.digital_tool}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h3>
          <div className="grid grid-cols-3 gap-4">
            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${assessmentId}/export-pdf`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    }
                  );

                  // Create blob URL and trigger download
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `SSEM_Report_${assessment.period_tag}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Error downloading PDF:', error);
                  showError('Failed to download PDF report. Please try again.');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF Report
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
            >
              Print Roadmap
            </Button>
            <Button
              onClick={() => navigate('/ssem')}
              variant="outline"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSEMRoadmap;
